import React, { useState, useEffect, useCallback } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5" data-testid="trust-strip-stars">
    {[...Array(5)].map((_, s) => (
      <Star
        key={s}
        size={16}
        className={s < n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
      />
    ))}
  </span>
);

const TrustStrip = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [quoteIdx, setQuoteIdx] = useState(0);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
      const data = await res.json();
      if (data.success) {
        setReviews(data.reviews || []);
        setAvg(data.average_rating || 0);
      }
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Rotate through real reviews for the mini-quote
  useEffect(() => {
    if (reviews.length < 2) return;
    const id = setInterval(() => setQuoteIdx((i) => (i + 1) % reviews.length), 4000);
    return () => clearInterval(id);
  }, [reviews.length]);

  const hasReal = reviews.length > 0;
  const current = hasReal ? reviews[quoteIdx % reviews.length] : null;

  const scrollToReviews = () => {
    document.getElementById('avis')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className="mt-[60px] md:mt-0 bg-white border-b border-gray-100"
      data-testid="home-trust-strip"
    >
      <div className="container">
        <button
          type="button"
          onClick={scrollToReviews}
          className="w-full flex flex-wrap items-center justify-center gap-x-3 gap-y-1 py-2.5 text-center cursor-pointer group"
          aria-label={t('trust.strip', 'Trusted by our clients')}
          data-testid="trust-strip-link"
        >
          <Stars n={hasReal ? Math.round(avg) : 5} />
          {hasReal ? (
            <span className="text-sm font-semibold text-[#0B3F84] group-hover:underline underline-offset-4" data-testid="trust-strip-rating">
              {avg}/5 · {reviews.length} {t('reviews.reviewsCount', 'reviews')}
            </span>
          ) : (
            <span className="text-sm font-semibold text-[#0B3F84] group-hover:underline underline-offset-4" data-testid="trust-strip-label">
              {t('trust.strip', 'Trusted by our clients')}
            </span>
          )}
          {current && (
            <>
              <span className="hidden sm:inline text-gray-300">•</span>
              <span
                key={quoteIdx}
                className="hidden sm:inline text-sm text-gray-500 italic max-w-md truncate trust-quote-fade"
                data-testid="trust-strip-quote"
              >
                “{current.message}” — {current.name}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TrustStrip;
